import React, { useEffect, useState } from 'react';
import { redirect, useNavigate, useParams } from "react-router-dom";

import { fetchGame } from '../../services/ledgerService';
import { AddPlayerModal } from "./AddPlayerModal";
import { AddTransactionModal } from "./AddTransactionModal";
import { GameHeader } from "./components/GameHeader";
import { GameTotals, RosterHeader } from "./components/GameTotals";
import { PlayerSearch } from "./components/PlayerSearch";
import { PlayerStats } from "./components/PlayerStats";
import { LedgerMobile } from "./mobile/LedgerMobile";
import { CashOutFlow } from "./CashOutFlow";
import { SettlementView } from "./SettlementView";
import { GROUPS, filterByGroup, groupCounts, emptyText } from "./mobile/playerGroups";
import { useHouseDispatch, useHouseState } from '../../contexts/HouseContext';
import { useIsMobile } from "../../hooks/useMediaQuery";

import 'react-bootstrap-typeahead/css/Typeahead.css';
import './Ledger.css';

export function GameLedger() {
    const navigate = useNavigate();
    const { loadGame } = useHouseDispatch();
    const { games } = useHouseState();
    const { gameId } = useParams();

    useEffect(() => {
        const fetchGameData = async () => {
            try {
                await loadGame(gameId);
            } catch (e) {
                navigate("/games");
            }
        };

        fetchGameData();
    }, [gameId]);

    if (!games[gameId]) {
        return <div className="ledger"><p>Loading game…</p></div>;
    }

    return <Ledger game={games[gameId]} />;
}

const Ledger = ({ game }) => {
    const navigate = useNavigate();
    const { sortPlayers, updatePendingTransaction } = useHouseDispatch();
    const isMobile = useIsMobile();

    const [showAddPlayer, setShowAddPlayer] = useState(false);
    const [showAddTransaction, setShowAddTransaction] = useState(false);
    const [activePlayerId, setActivePlayerId] = useState(null);
    const [cashOutPlayerId, setCashOutPlayerId] = useState(null);
    const [showSettlement, setShowSettlement] = useState(false);
    const [typeaheadInput, setTypeaheadInput] = useState("");
    const [groupTab, setGroupTab] = useState("all");
    const [sort, updateSort] = useState({ sortBy: null, sortOrder: null });

    const counts = groupCounts(game.players);
    const viewablePlayers = filterByGroup(game.players, groupTab).filter((p) =>
        p.name.toLowerCase().includes(typeaheadInput.toLowerCase()),
    );

    // Open the shared transaction modal with the type preselected — the
    // mobile tile has distinct + buy-in / ↗ cash-out actions, where the
    // desktop row opens the modal type-agnostic and the user toggles.
    const openTransaction = async (player, transactionType) => {
        await updatePendingTransaction(game.id, {
            ...game.pendingTransaction,
            transactionType,
        });
        setActivePlayerId(player.id);
        setShowAddTransaction(true);
    };

    const onSort = (sortBy) => {
        let sortOrder;
        let confirmedSortBy = sortBy;

        if (sort.sortBy === null || sortBy !== sort.sortBy) {
            sortOrder = "desc";
        } else if (sortBy === sort.sortBy && sort.sortOrder !== "asc") {
            sortOrder = "asc";
        } else {
            confirmedSortBy = null;
            sortOrder = null;
        }
        updateSort({ sortBy: confirmedSortBy, sortOrder });
        sortPlayers(game.id, confirmedSortBy, sortOrder);
    };

    // Shared between both layouts — the modals read pending state from the
    // House context, so the same instances serve the mobile tiles and the
    // desktop rows.
    const modals = (
        <>
            <AddPlayerModal
                gameId={game.id}
                players={game.players}
                show={showAddPlayer}
                handleClose={() => setShowAddPlayer(false)}
            />
            <AddTransactionModal
                game={game}
                player={game.players.find((p) => p.id === activePlayerId)}
                show={showAddTransaction}
                handleClose={() => setShowAddTransaction(false)}
                allowCashOut={!isMobile}
            />
        </>
    );

    if (isMobile) {
        const cashOutTarget = game.players.find((p) => p.id === cashOutPlayerId);
        return (
            <>
                <LedgerMobile
                    game={game}
                    onBack={() => navigate("/games")}
                    onAddPlayer={() => setShowAddPlayer(true)}
                    onBuyIn={(p) => openTransaction(p, "BUY_IN")}
                    onCashOut={(p) => setCashOutPlayerId(p.id)}
                    onSettle={() => setShowSettlement(true)}
                />
                {modals}
                {cashOutTarget && (
                    <CashOutFlow
                        game={game}
                        player={cashOutTarget}
                        onClose={() => setCashOutPlayerId(null)}
                    />
                )}
                {showSettlement && (
                    <SettlementView
                        game={game}
                        onClose={() => setShowSettlement(false)}
                    />
                )}
            </>
        );
    }

    return (
        <main className="ledger">
            <GameHeader game={game} onClick={() => setShowAddPlayer(true)} />
            <GameTotals game={game} />
            <LedgerTabs tab={groupTab} counts={counts} onSelect={setGroupTab} />
            <PlayerSearch
                value={typeaheadInput}
                setTypeaheadInput={setTypeaheadInput}
            />
            <div className="ledger-roster">
                <RosterHeader sort={sort} onSort={onSort} />
                {viewablePlayers.length === 0 ? (
                    <div className="ledger-roster-empty">
                        {emptyText(groupTab, game.players.length, typeaheadInput.trim())}
                    </div>
                ) : (
                    viewablePlayers.map((p) => (
                        <PlayerStats
                            key={p.id}
                            gameId={game.id}
                            player={p}
                            handleShowAddTransaction={() => openTransaction(p, "BUY_IN")}
                        />
                    ))
                )}
            </div>
            {modals}
        </main>
    );
};

function LedgerTabs({ tab, counts, onSelect }) {
    return (
        <div className="ledger-tabs" role="tablist">
            {GROUPS.map((g) => (
                <button
                    key={g.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === g.key}
                    className={`ledger-tab${tab === g.key ? " is-on" : ""}`}
                    onClick={() => onSelect(g.key)}
                >
                    {g.label}
                    <span className="ledger-tab-count">{counts[g.key]}</span>
                </button>
            ))}
        </div>
    );
}

export async function loader({ params }) {
    let game = null;
    try {
        game = await fetchGame({ gameId: params.gameId });
    } catch (e) {
        console.error("Error fetching house data:", e);
        return redirect("/login");
    }

    if (!game) {
        return redirect("/games");
    }

    return { game };
}
